import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { flow, types } from "mobx-state-tree";
import { AuthStore } from "./AuthStore";
import { BuildingStore } from "./BuildingStore";
import { UserStore } from "./UserStore";
import { OrganizationStore } from "./OrganizationStore";
import { DocumentStore } from "./documents/DocumentStore";
import { BuildingDataAttributesStore } from "./admin/BuildingDataAttributesStore";
import { ClassHierarchyStore } from "./admin/ClassHierarchyStore";
import { DocumentCategoriesStore } from "./admin/DocumentCategoriesStore";
import { DesignDisciplinesStore } from "./admin/DesignDisciplinesStore";
import { DesignStagesStore } from "./admin/DesignStagesStore";
import { DocumentTemplatesStore } from "./admin/DocumentTemplatesStore";
import { PipelinesStore } from "./admin/PipelinesStore";
import { PromptsStore } from "./admin/PromptsStore";
import { RolesStore } from "./admin/RolesStore";
import { ChatStore } from "./chat/ChatStore";
import { UploadQueueStore } from "./uploadQueue/UploadQueueStore";
import { PMStore } from "./PMStore";
import { MessagesStore } from "./messages/MessagesStore";
import { ProjectFolderStore } from "./projects/ProjectFolderStore";
import { BuildingDataWidgetStore } from "./ui/buildingDataWidgetStore";
import { BuildingScopeStore } from "./ui/buildingScopeStore";
import { BuildingViewModeStore } from "./ui/buildingViewModeStore";
import { IfcModelStore } from "./IfcModelStore";

/**
 * The root store model for the application.
 * This serves as the central state container that holds all domain-specific stores.
 * Currently includes AuthStore with plans to add more domain stores in the future.
 *
 * Future stores to be added:
 * - organizationStore: Organization settings and data
 */
export const RootStore = types
  .model("RootStore", {
    /**
     * Authentication store for managing user sessions and permissions.
     * Initialized with empty state by default.
     */
    authStore: types.optional(AuthStore, {}),
    /** User store for managing user profile data */
    userStore: types.optional(UserStore, {}),
    /** Domain store: Building operations and state */
    buildingStore: types.optional(BuildingStore, {}),
    /** Domain store: Document management */
    documentStore: types.optional(DocumentStore, {}),
    /** Organization store for managing organization settings and users */
    organizationStore: types.optional(OrganizationStore, {}),
    /** Admin store: Building data attributes management */
    buildingDataAttributesStore: types.optional(BuildingDataAttributesStore, {}),
    /** Admin store: Class hierarchy (categories + sub-classes) management */
    classHierarchyStore: types.optional(ClassHierarchyStore, {}),
    /** Admin store: Document categories management */
    documentCategoriesStore: types.optional(DocumentCategoriesStore, {}),
    /** Admin store: Design disciplines management */
    designDisciplinesStore: types.optional(DesignDisciplinesStore, {}),
    /** Admin store: Design stages management */
    designStagesStore: types.optional(DesignStagesStore, {}),
    /** Admin store: Document templates + their classification links */
    documentTemplatesStore: types.optional(DocumentTemplatesStore, {}),
    /** Admin store: Pipelines management */
    pipelinesStore: types.optional(PipelinesStore, {}),
    /** Admin store: Prompts management */
    promptsStore: types.optional(PromptsStore, {}),
    /** Admin store: Roles + role-permissions management (platform admin only) */
    rolesStore: types.optional(RolesStore, {}),
    /** Chat store for managing building chatbot interactions */
    chatStore: types.optional(ChatStore, {}),
    /** Upload queue store for bulk file uploads with concurrency control */
    uploadQueueStore: types.optional(UploadQueueStore, {}),
    /** PM store: Project management (issues, tasks, protocols, brief) */
    pmStore: types.optional(PMStore, {}),
    /** Per-project message board (dashboard Messages widget) */
    messagesStore: types.optional(MessagesStore, {}),
    /** Project + folder option lookups for chatbot file-routing UI */
    projectFolderStore: types.optional(ProjectFolderStore, {}),
    /** UI store: persists building data widget expand/collapse state */
    buildingDataWidgetStore: types.optional(BuildingDataWidgetStore, {}),
    /** UI store: persists the global building scope (whole-building vs project) */
    buildingScopeStore: types.optional(BuildingScopeStore, {}),
    /** UI store: persists the sidebar Building/Projects view mode per building */
    buildingViewModeStore: types.optional(BuildingViewModeStore, {}),
    /** Domain store: 3D IFC viewer — active model + selection state (WHA-3537) */
    ifcModelStore: types.optional(IfcModelStore, {}),
    /**
     * Cache version counter for M2M relationships.
     * Increments whenever M2M relationships change.
     * Components can check this to invalidate their caches.
     */
    m2mCacheVersion: types.optional(types.number, 0),
  })
  .actions((self) => ({
    /**
     * Reset all stores to their initial state.
     * Useful for logout or clearing application state.
     * Note: AuthStore.logout() handles UserStore cleanup internally.
     */
    resetStores: flow(function* resetStores(): Generator<Promise<void>, void, unknown> {
      yield self.authStore.logout();
      // AuthStore.logout() already clears UserStore
      // Future: Reset other stores as they are added
    }),

    /**
     * Invalidate all M2M relationship caches globally.
     * Call this after any M2M relationship update to ensure all components refresh.
     */
    invalidateM2MCache() {
      self.m2mCacheVersion += 1;
    },
  }))
  .views((self) => ({
    /**
     * Global authentication check.
     * Delegates to authStore for authentication status.
     */
    get isAuthenticated(): boolean {
      return self.authStore.isAuthenticated;
    },

    /**
     * Get current user ID if authenticated.
     * Returns null if not authenticated.
     */
    get currentUserId(): string | null {
      return self.authStore.userId;
    },
  }));

// Export the TypeScript type for the store instance
export type RootStoreInstance = Instance<typeof RootStore>;
export type RootStoreSnapshotIn = SnapshotIn<typeof RootStore>;
export type RootStoreSnapshotOut = SnapshotOut<typeof RootStore>;
